import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import { ExdBackButton } from "../../ExDashboard/components/ExdControls";
import { ExdEmpty } from "../../ExDashboard/components/exdMui";
import {
  applyEnterpriseChartTheme,
  HC_CONTAINER_PROPS,
} from "../../ExDashboard/components/chartTheme";
import { MD, mdScrollbarSx } from "./mdTheme";
import { MdWidgetCard } from "./MdCardShell";
import MdClientRankRow from "./MdClientRankRow";

const extractPiePoints = (chartOptions) => {
  const series = chartOptions?.series;
  if (!Array.isArray(series) || !series.length) return [];

  const first = series[0];
  const raw = Array.isArray(first?.data) ? first.data : series;

  return (raw || [])
    .map((point, index) => {
      if (point && typeof point === "object" && !Array.isArray(point)) {
        return {
          name: point.name || `Item ${index + 1}`,
          y: Number(point.y ?? point.value) || 0,
          color: point.color || MD.chart[index % MD.chart.length],
        };
      }
      return null;
    })
    .filter((p) => p && p.y > 0);
};

/** Normalize API / mapped fleet rows into a stable list shape */
const normalizeFleetRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((d, index) => {
      const label =
        d.label ||
        d.name ||
        d.contractNumber ||
        d.accountName ||
        d.clientName ||
        `Client ${index + 1}`;
      const value = Number(
        d.value ?? d.accessVehiclesCount ?? d.y ?? d.count ?? d.key ?? 0,
      );
      const barPct = Number(d.barPct ?? d.salesPercentage ?? d.countPercentage);
      return {
        key: `${label}-${index}`,
        label,
        value: Number.isFinite(value) ? value : 0,
        barPct: Number.isFinite(barPct) ? barPct : 0,
        raw: d.raw || d,
      };
    })
    .filter((r) => r.label);

const FleetSizeCard = ({
  translate,
  loading,
  totalFleetSize,
  rows = [],
  openGraph,
  chartOptions,
  onRowClick,
  onBack,
}) => {
  const listRows = useMemo(() => normalizeFleetRows(rows), [rows]);

  const maxValue = useMemo(
    () => Math.max(...listRows.map((r) => r.value), 0),
    [listRows],
  );

  const piePoints = useMemo(
    () => (openGraph ? extractPiePoints(chartOptions) : []),
    [openGraph, chartOptions],
  );

  const themedChart = useMemo(() => {
    if (!openGraph || !piePoints.length) return null;

    return applyEnterpriseChartTheme({
      chart: {
        type: "pie",
        height: 260,
        backgroundColor: "transparent",
        spacing: [8, 8, 8, 8],
      },
      title: { text: null },
      tooltip: {
        pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "58%",
          borderWidth: 2,
          borderColor: "#fff",
          dataLabels: {
            enabled: true,
            distance: 12,
            format: "{point.name}: {point.percentage:.0f}%",
            style: {
              color: MD.fg,
              fontSize: "11px",
              fontWeight: "600",
              textOutline: "none",
            },
          },
          showInLegend: false,
        },
      },
      series: [
        {
          type: "pie",
          name: "Fleet",
          data: piePoints.map((p, i) => ({
            name: p.name,
            y: p.y,
            color: MD.chart[i % MD.chart.length],
          })),
        },
      ],
      legend: { enabled: false },
      credits: { enabled: false },
    });
  }, [openGraph, piePoints]);

  const showChart = !!(openGraph && themedChart);

  return (
    <MdWidgetCard
      loading={loading}
      sx={{ overflow: "hidden", minWidth: 0 }}
      eyebrow={translate("clientVehiclesCount") || "Client Fleet Size"}
      infoTitle={
        translate("clientFleetSizeInfo") ||
        "Vehicles registered per client. Click a client to see the fleet by vehicle type."
      }
      title={
        showChart
          ? translate("byVehicleType") || "By vehicle type"
          : translate("byClient") || "By client"
      }
      headValue={
        !showChart ? (
          <Box sx={{ textAlign: "right", minWidth: 0 }}>
            <Typography
              component="span"
              noWrap
              sx={{
                display: "block",
                fontFamily: MD.fontHead,
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              {totalFleetSize ? nFormatter(totalFleetSize, 2) : "0"}
            </Typography>
            <Typography component="span" sx={{ color: MD.muted, fontSize: 11 }}>
              {translate("vehicles") || "vehicles"}
            </Typography>
          </Box>
        ) : null
      }
      controls={
        openGraph ? (
          <ExdBackButton onClick={onBack}>
            {translate("sharedBack") || "BACK"}
          </ExdBackButton>
        ) : null
      }
    >
      {showChart ? (
        <Box sx={{ height: 260, width: "100%", mt: 0.5, overflow: "hidden" }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={themedChart}
            containerProps={{
              ...HC_CONTAINER_PROPS,
              style: { height: 260, width: "100%", overflow: "hidden" },
            }}
            immutable
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            gap: 0.625,
            maxHeight: 300,
            minHeight: 160,
            minWidth: 0,
            mt: 0.5,
            overflowX: "hidden",
            overflowY: "auto",
            pr: 0.5,
            width: "100%",
            ...mdScrollbarSx,
          }}
        >
          {listRows.length ? (
            listRows.map((row, index) => {
              const barPct =
                row.barPct > 0
                  ? row.barPct
                  : maxValue
                    ? (row.value / maxValue) * 100
                    : 0;
              return (
                <MdClientRankRow
                  key={row.key}
                  label={row.label}
                  value={row.value.toLocaleString()}
                  barPct={barPct}
                  color={MD.chart[index % MD.chart.length]}
                  onClick={
                    onRowClick
                      ? () => onRowClick(row.raw)
                      : undefined
                  }
                />
              );
            })
          ) : (
            <ExdEmpty />
          )}
        </Box>
      )}
    </MdWidgetCard>
  );
};

export default FleetSizeCard;
